import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import LazyLoader from "./LazyLoader";

const getFileType = (url = "") => {
  const ext = url.split(".").pop().toLowerCase();

  if (["pdf"].includes(ext)) return "pdf";
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "image";
  if (["doc", "docx"].includes(ext)) return "doc";
  if (["xls", "xlsx"].includes(ext)) return "excel";

  return "other";
};

const FilePreviewModal = ({ show, handleClose, file }) => {

  const fileUrl = file?.url || "";
  const htmlContent = file?.html || "";

  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    setRotation(0);
    setZoom(1);
    setLoading(true);
    setLoadError(false);
  }, [file, show]);

  // 🔁 ROTATE
  const rotateLeft = () => setRotation(prev => prev - 90);
  const rotateRight = () => setRotation(prev => prev + 90);

  // 🔍 ZOOM
  const zoomIn = () => setZoom(prev => Math.min(prev + 0.2, 3));
  const zoomOut = () => setZoom(prev => Math.max(prev - 0.2, 0.5));
  const resetZoom = () => setZoom(1);

  // ⬇ DOWNLOAD
  const handleDownload = async () => {
    if (!fileUrl) return;

    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = fileUrl.split("/").pop();
    link.click();
  };

  // 🖨 PRINT
  const handlePrint = () => {
    const iframe = document.querySelector("iframe");
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>

      <Modal.Header>
        <Modal.Title>Bill / Document Preview</Modal.Title>
        <Button className="btn-close" onClick={handleClose}></Button>
      </Modal.Header>

      {/* ACTION BAR */}
      {(fileUrl || htmlContent || file?.files) && (
        <div className="d-flex justify-content-between align-items-center px-3 pt-2 flex-wrap gap-2">

          {/* LEFT */}
          <div className="d-flex gap-2">

            {(fileUrl || file?.files) && (
              <>
                <Button size="sm" variant="primary" onClick={rotateLeft}>
                  ⟲ Rotate
                </Button>
                <Button size="sm" variant="primary" onClick={rotateRight}>
                  ⟳ Rotate
                </Button>
              </>
            )}

            <Button size="sm" variant="primary" onClick={zoomOut}>
              ➖ Zoom
            </Button>
            <Button size="sm" variant="primary" onClick={zoomIn}>
              ➕ Zoom
            </Button>
            <Button size="sm" variant="outline-primary" onClick={resetZoom}>
              Reset
            </Button>
          </div>

          {/* RIGHT */}
          <div className="d-flex gap-2">
            {fileUrl && (
              <Button size="sm" variant="success" onClick={handleDownload}>
                ⬇ Download
              </Button>
            )}

            <Button size="sm" variant="primary" onClick={handlePrint}>
              🖨 Print
            </Button>
          </div>
        </div>
      )}

      {/* BODY */}
      <Modal.Body
        style={{
          maxHeight: "80vh",
          overflow: "auto",
          position: "relative"
        }}
      >
        {loading && !loadError && (
          <LazyLoader text="Loading Document..." />
        )}

        {/* MULTI FILE */}
        {file?.files && (
          <>
            {file.files.map((f, i) => {
              const type = getFileType(f.url);

              return (
                <div key={i} className="text-center mb-3">

                  {type === "pdf" ? (
                    <embed
                      src={f.url}
                      type="application/pdf"
                      width="100%"
                      height="600px"
                      onLoad={() => setLoading(false)}
                    />
                  ) : (
                    <img
                      src={f.url}
                      alt=""
                      onLoad={() => setLoading(false)}
                      style={{
                        maxWidth: "100%",
                        transform: `scale(${zoom}) rotate(${rotation}deg)`
                      }}
                    />
                  )}

                </div>
              );
            })}
          </>
        )}

        {/* NO FILE */}
        {!file?.files && !htmlContent && !fileUrl && (
          <p className="text-center text-muted">No document selected</p>
        )}

        {/* PDF */}
        {!file?.files && !htmlContent && fileUrl && getFileType(fileUrl) === "pdf" && (
          <embed
            src={fileUrl}
            type="application/pdf"
            width="100%"
            height="600px"
            onLoad={() => setLoading(false)}
          />
        )}

        {/* IMAGE */}
        {!file?.files && !htmlContent && fileUrl && getFileType(fileUrl) === "image" && (
          <div style={{ textAlign: "center" }}>

            <img
              src={fileUrl}
              alt="Preview"
              onLoad={() => setLoading(false)}
              onError={() => {
                setLoading(false);
                setLoadError(true);
              }}
              style={{
                maxWidth: "100%",
                transform: `scale(${zoom}) rotate(${rotation}deg)`,
                display: loadError ? "none" : "block"
              }}
            />

            {loadError && (
              <div className="text-center p-4">
                <i
                  className="fas fa-image"
                  style={{
                    fontSize: "50px",
                    color: "#ccc"
                  }}
                />
                <h5 className="mt-3 text-muted">
                  No Preview Available
                </h5>
              </div>
            )}

          </div>
        )}

        {/* DOC / EXCEL */}
        {!file?.files && !htmlContent && fileUrl &&
          (getFileType(fileUrl) === "doc" || getFileType(fileUrl) === "excel") && (
            <iframe
              src={`https://docs.google.com/gview?url=${fileUrl}&embedded=true`}
              width="100%"
              height="600px"
              title={`doc-preview-${fileUrl}`}   // ✅ FIXED ERROR
              onLoad={() => setLoading(false)}
            />
          )}

      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FilePreviewModal;